import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";
import { toNumber } from "../../utils/formatMoney";
import { formatUkDateTime } from "../../utils/dateUtils";

type DeadLegStatus = "live" | "expired" | "disabled";

type DeadLegRow = {
  id: string;
  referenceNumber: number;
  from: string | null;
  until: string | null;
  atPostCode: string | null;
  atRadiusMiles: number | null;
  goingTo: string;
  discountPercent: number | null;
  status: DeadLegStatus;
  linkedFullPriceBookingRef: string | null;
  dldBookingRef: string | null;
};

type ListResponse = {
  items: DeadLegRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return formatUkDateTime(iso);
}

export default function DeadLegs() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get<ListResponse>("/deadlegs", {
        params: { page, pageSize: 10, status: "all" },
      });
      setData(res.data);
    } catch {
      setMessage("Failed to load dead leg discounts");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStop = async (id: string) => {
    if (!window.confirm("Stop this dead leg discount?")) return;
    try {
      await axiosInstance.post(`/deadlegs/${id}/stop`);
      setMessage("Discount stopped");
      await load();
    } catch {
      setMessage("Failed to stop discount");
    }
  };

  const renderPagination = () => {
    if (!data || data.totalPages <= 1) return null;
    const pages: number[] = [];
    for (let i = 1; i <= Math.min(data.totalPages, 5); i++) pages.push(i);

    return (
      <div className="flex justify-center items-center gap-1 mt-6">
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPage(p)}
            className={`min-w-[2rem] px-2 py-1 text-sm ${
              page === p ? "font-bold text-gray-900" : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {p}
          </button>
        ))}
        {data.totalPages > 5 && <span className="text-gray-400 px-1">…</span>}
        {page < data.totalPages && (
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            className="px-2 py-1 text-gray-500 hover:text-gray-800"
          >
            →
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <PageBreadcrumb pageTitle="Dead Leg Discounts" />
        <Link
          to="/dead-legs/add"
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 text-sm font-medium"
        >
          Add Dead Leg Discount
        </Link>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        You can see a record of all the trips you applied Dead Leg Discounts to. This table
        shows current and previous jobs with DLDs, and the original trip each was linked to.
      </p>

      {message && (
        <p
          className={`mb-4 text-sm ${
            message.includes("Failed") ? "text-red-600" : "text-green-600"
          }`}
        >
          {message}
        </p>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Dead Leg Discount ID
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">From</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Until</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">At</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Going to</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Discount Type</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Linked to full price booking
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">DLD booking ref</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : !data?.items.length ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  No dead leg discounts yet.{" "}
                  <Link to="/dead-legs/add" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
                    Add one
                  </Link>
                </td>
              </tr>
            ) : (
              data.items.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50/80">
                  <td className="px-4 py-3 font-mono">{row.referenceNumber}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(row.from)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(row.until)}</td>
                  <td className="px-4 py-3">
                    {row.atPostCode
                      ? `Within ${row.atRadiusMiles ?? 0} miles of ${row.atPostCode}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3">{row.goingTo}</td>
                  <td className="px-4 py-3">
                    {(() => {
                      const pct = toNumber(row.discountPercent);
                      return pct != null ? `${pct.toFixed(2)}%` : "—";
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        row.status === "live"
                          ? "text-red-600 font-medium capitalize"
                          : "text-gray-700 capitalize"
                      }
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {row.status === "live" ? (
                      <button
                        type="button"
                        onClick={() => handleStop(row.id)}
                        className="text-red-600 hover:underline font-medium"
                      >
                        Stop
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {row.linkedFullPriceBookingRef ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {row.dldBookingRef ?? "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {renderPagination()}
      </div>
    </div>
  );
}
